import React, { useEffect, useState, useMemo, useRef } from "react";
import { useMediaQuery } from "react-responsive";
import { useHistory } from "react-router-dom";
import SideBar from "../../components/SideBar";
import { Components } from "../../assets/Dimensions";
import { toast } from "react-toastify";
import Loader from "@material-ui/core/CircularProgress";
import { GetCMSReport } from "../../api/CMS";

// ─── Column definitions ───────────────────────────────────────────────────────

const TASK_STATUS_COLUMNS = [
  { key: "WorkList",      label: "İş",           sortable: true },
  { key: "CourseName",    label: "Ders",          sortable: true },
  { key: "Todo",          label: "Todo",          sortable: true, numeric: true },
  { key: "InProgress",    label: "InProgress",    sortable: true, numeric: true },
  { key: "PendingReview", label: "PendingReview", sortable: true, numeric: true },
  { key: "InReview",      label: "InReview",      sortable: true, numeric: true },
  { key: "Done",          label: "Done",          sortable: true, numeric: true },
  { key: "_onErrorWithDone", label: "OnError",    sortable: true, numeric: true },
  { key: "MarkAsDone",    label: "InMarkAsDoneReview",      sortable: true, numeric: true },
  { key: "TotalCount",    label: "Toplam",        sortable: true, numeric: true },
  { key: "_donePercent",   label: "% Bitti",       sortable: true, numeric: true, noSum: true },
];

const TOPIC_STATUS_COLUMNS = [
  { key: "WorkList",     label: "İş",           sortable: true },
  { key: "CourseName",   label: "Ders",          sortable: true },
  { key: "UserName",     label: "Kullanıcı",     sortable: true },
  { key: "None",         label: "None",          sortable: true, numeric: true },
  { key: "Success",      label: "Success",       sortable: true, numeric: true },
  { key: "HasError",     label: "hasError",      sortable: true, numeric: true },
  { key: "Skipped",      label: "skipped",       sortable: true, numeric: true },
  { key: "Incompatible", label: "incompatible",  sortable: true, numeric: true },
  { key: "TotalCount",   label: "Toplam",        sortable: true, numeric: true },
  //{ key: "_donePercent",   label: "% Bitti",       sortable: true, numeric: true, noSum: true },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

// null in filters means "all selected" (no filter applied)
// []   means "none checked" (0 rows pass — user explicitly unchecked everything)
const applyFilters = (data, filters) =>
  data.filter((row) =>
    Object.entries(filters).every(([key, selected]) =>
      selected === null ? true : selected.includes(row[key])
    )
  );

const applySort = (data, sort) => {
  if (!sort.key || !sort.dir) return data;
  return [...data].sort((a, b) => {
    const va = a[sort.key];
    const vb = b[sort.key];
    if (typeof va === "number" && typeof vb === "number") {
      return sort.dir === "asc" ? va - vb : vb - va;
    }
    const sa = String(va ?? "").localeCompare(String(vb ?? ""), "tr", { sensitivity: "base" });
    return sort.dir === "asc" ? sa : -sa;
  });
};

const exportToCSV = (columns, data, filename) => {
  if (!data || data.length === 0) {
    toast.warning("Dışa aktarılacak veri yok.");
    return;
  }
  const header = columns.map((c) => c.label).join(",");
  const rows = data.map((row) =>
    columns
      .map((col) => {
        let val = row[col.key] ?? "";
        if (typeof val === "string" && (val.includes(",") || val.includes('"'))) {
          val = `"${val.replace(/"/g, '""')}"`;
        }
        return val;
      })
      .join(",")
  );
  const bom = "﻿"; // UTF-8 BOM for Turkish chars in Excel
  const blob = new Blob([bom + [header, ...rows].join("\n")], {
    type: "text/csv;charset=utf-8;",
  });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.setAttribute("download", filename);
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

// ─── DropdownFilter ───────────────────────────────────────────────────────────
// Rendered ABOVE the table (outside the overflow container) to avoid clipping.

const DropdownFilter = ({ columnKey, label, optionsData, selectedValues, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [hoveredValue, setHoveredValue] = useState(null);
  const containerRef = useRef(null);

  // Unique sorted values derived from cascaded data (other filters already applied)
  const uniqueValues = useMemo(() => {
    const set = new Set(optionsData.map((row) => row[columnKey]).filter((v) => v != null));
    return [...set].sort((a, b) =>
      String(a).localeCompare(String(b), "tr", { sensitivity: "base" })
    );
  }, [optionsData, columnKey]);

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [isOpen]);

  // null = all selected (no filter); [] = none checked; ["A"] = only A
  const isAllSelected = selectedValues === null;
  const isFiltered = selectedValues !== null;
  const isChecked = (val) => isAllSelected || selectedValues.includes(val);

  const clearFilter = () => onChange(null); // reset to "show all"

  const toggleSelectAll = () => {
    if (isAllSelected) {
      onChange([]); // uncheck all checkboxes
    } else {
      onChange(null); // check all (reset filter)
    }
  };

  const toggleValue = (val) => {
    if (isAllSelected) {
      // Uncheck one item → all others remain explicitly selected
      onChange(uniqueValues.filter((v) => v !== val));
    } else if (selectedValues.includes(val)) {
      // Uncheck: remove (empty array = 0 rows shown, intentional)
      onChange(selectedValues.filter((v) => v !== val));
    } else {
      // Check: add; if all values now selected → collapse back to null
      const next = [...selectedValues, val];
      onChange(next.length === uniqueValues.length ? null : next);
    }
  };

  return (
    <div ref={containerRef} style={{ position: "relative" }}>
      {/* Trigger button */}
      <button
        onClick={() => setIsOpen((o) => !o)}
        style={{
          padding: "7px 12px",
          fontSize: 13,
          border: `1.5px solid ${isFiltered ? "#2563eb" : "#d1d5db"}`,
          borderRadius: 6,
          backgroundColor: isFiltered ? "#eff6ff" : "#fff",
          color: isFiltered ? "#1d4ed8" : "#374151",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          gap: 6,
          fontWeight: isFiltered ? 600 : "normal",
          whiteSpace: "nowrap",
          minWidth: 120,
        }}
      >
        <span style={{ flex: 1, textAlign: "left" }}>
          {label}
          {isFiltered && (
            <span
              style={{
                marginLeft: 6,
                backgroundColor: selectedValues.length === 0 ? "#ef4444" : "#2563eb",
                color: "#fff",
                borderRadius: 10,
                padding: "0 6px",
                fontSize: 11,
                fontWeight: 700,
              }}
            >
              {selectedValues.length}
            </span>
          )}
        </span>
        <span style={{ opacity: 0.6, fontSize: 11 }}>{isOpen ? "▲" : "▼"}</span>
      </button>

      {/* Dropdown panel */}
      {isOpen && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            left: 0,
            zIndex: 500,
            backgroundColor: "#fff",
            border: "1px solid #d1d5db",
            borderRadius: 8,
            boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
            minWidth: 240,
            maxHeight: 320,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          {/* Header row: Tümünü Seç + Tümünü Kaldır */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "10px 14px",
              borderBottom: "2px solid #e5e7eb",
              backgroundColor: "#f9fafb",
              flexShrink: 0,
              gap: 8,
            }}
          >
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                cursor: "pointer",
                fontSize: 13,
                fontWeight: 700,
                color: "#111827",
              }}
            >
              <input
                type="checkbox"
                checked={isAllSelected}
                onChange={toggleSelectAll}
                style={{ width: 15, height: 15, cursor: "pointer" }}
              />
              Tümünü Seç
              <span style={{ fontSize: 11, color: "#9ca3af", fontWeight: 400 }}>
                {uniqueValues.length} öğe
              </span>
            </label>
            {/* {isFiltered && (
              <button
                onClick={clearFilter}
                style={{
                  flexShrink: 0,
                  padding: "3px 10px",
                  fontSize: 12,
                  border: "1px solid #fca5a5",
                  borderRadius: 4,
                  backgroundColor: "#fef2f2",
                  color: "#dc2626",
                  cursor: "pointer",
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                }}
              >
                Tümünü Kaldır
              </button>
            )} */}
          </div>

          {/* Scrollable value list */}
          <div style={{ overflowY: "auto", flex: 1 }}>
            {uniqueValues.map((val) => (
              <label
                key={val}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "7px 14px",
                  cursor: "pointer",
                  fontSize: 13,
                  color: "#374151",
                  backgroundColor: hoveredValue === val ? "#f3f4f6" : "",
                }}
                onMouseEnter={() => setHoveredValue(val)}
                onMouseLeave={() => setHoveredValue(null)}
              >
                <input
                  type="checkbox"
                  checked={isChecked(val)}
                  onChange={() => toggleValue(val)}
                  style={{ width: 15, height: 15, cursor: "pointer", flexShrink: 0 }}
                />
                <span
                  style={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    flex: 1,
                  }}
                >
                  {val}
                </span>
                {/* "Only This" shortcut — visible on row hover */}
                {hoveredValue === val && (
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onChange([val]);
                      setIsOpen(false);
                    }}
                    style={{
                      flexShrink: 0,
                      padding: "2px 7px",
                      fontSize: 11,
                      border: "1px solid #2563eb",
                      borderRadius: 4,
                      backgroundColor: "#eff6ff",
                      color: "#1d4ed8",
                      cursor: "pointer",
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                    }}
                  >
                    Sadece Bu
                  </button>
                )}
              </label>
            ))}
          </div>

        </div>
      )}
    </div>
  );
};

// ─── AnalysisTable ────────────────────────────────────────────────────────────

const AnalysisTable = ({ columns, data, filters, onFilterChange, filename }) => {
  const [sort, setSort] = useState({ key: null, dir: null });

  const handleHeaderClick = (col) => {
    if (!col.sortable) return;
    setSort((prev) => {
      if (prev.key !== col.key) return { key: col.key, dir: "asc" };
      if (prev.dir === "asc") return { key: col.key, dir: "desc" };
      return { key: null, dir: null };
    });
  };

  const getSortIcon = (colKey) => {
    if (sort.key !== colKey) return "↕";
    return sort.dir === "asc" ? "↑" : "↓";
  };

  const filteredData = useMemo(() => applyFilters(data, filters), [data, filters]);
  const displayData = useMemo(() => applySort(filteredData, sort), [filteredData, sort]);

  const anyFilterActive = Object.values(filters).some((v) => v !== null);
  const filterableColumns = columns.filter((c) => !c.numeric);

  // For each filterable column, compute options from data filtered by all OTHER active filters.
  // This gives cascading behaviour: selecting a WorkList filter narrows CourseName options and vice-versa.
  const optionsDataByKey = useMemo(() => {
    const result = {};
    filterableColumns.forEach((col) => {
      const otherFilters = Object.fromEntries(
        Object.entries(filters).filter(([k]) => k !== col.key)
      );
      result[col.key] = applyFilters(data, otherFilters);
    });
    return result;
  }, [data, filters]); // eslint-disable-line react-hooks/exhaustive-deps

  const clearAllFilters = () =>
    filterableColumns.forEach((col) => onFilterChange(col.key, null));

  return (
    <div>
      {/* ── Filter bar ─────────────────────────────────────────────────── */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          marginBottom: 14,
          flexWrap: "wrap",
        }}
      >
        {filterableColumns.map((col) => (
          <DropdownFilter
            key={col.key}
            columnKey={col.key}
            label={col.label}
            optionsData={optionsDataByKey[col.key]}
            selectedValues={filters[col.key]}
            onChange={(val) => onFilterChange(col.key, val)}
          />
        ))}

        {anyFilterActive && (
          <button
            onClick={clearAllFilters}
            style={{
              padding: "7px 12px",
              fontSize: 13,
              border: "1px solid #fca5a5",
              borderRadius: 6,
              backgroundColor: "#fef2f2",
              color: "#dc2626",
              cursor: "pointer",
              fontWeight: 600,
            }}
          >
            ✕ Tüm Filtreleri Temizle
          </button>
        )}

        {/* Row count + active sort info + export — right side */}
        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 12 }}>
          <span style={{ fontSize: 13, color: "#6b7280", whiteSpace: "nowrap" }}>
            {filteredData.length} / {data.length} satır
            {sort.key && (
              <span style={{ color: "#2563eb", marginLeft: 6 }}>
                · {sort.key} {sort.dir === "asc" ? "↑" : "↓"}
              </span>
            )}
          </span>
          <button
            onClick={() => exportToCSV(columns, displayData, filename)}
            style={{
              padding: "7px 14px",
              backgroundColor: "#16a34a",
              color: "#fff",
              border: "none",
              borderRadius: 6,
              cursor: "pointer",
              fontWeight: 600,
              fontSize: 13,
              whiteSpace: "nowrap",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#15803d")}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#16a34a")}
          >
            ⬇ Excel'e Aktar
          </button>
        </div>
      </div>

      {/* ── Table ──────────────────────────────────────────────────────── */}
      <div
        style={{
          overflowX: "auto",
          borderRadius: 8,
          boxShadow: "0 1px 4px rgba(0,0,0,0.1)",
        }}
      >
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ backgroundColor: "#1e3a5f" }}>
              {columns.map((col) => (
                <th
                  key={col.key}
                  onClick={() => handleHeaderClick(col)}
                  style={{
                    position: "sticky",
                    top: 0,
                    zIndex: 10,
                    backgroundColor: "#1e3a5f",
                    padding: "11px 14px",
                    textAlign: col.numeric ? "right" : "left",
                    color: "#fff",
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                    borderRight: "1px solid #2d5282",
                    cursor: col.sortable ? "pointer" : "default",
                    userSelect: "none",
                    minWidth: col.numeric ? 80 : 140,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: col.numeric ? "flex-end" : "space-between",
                      gap: 6,
                    }}
                  >
                    <span>{col.label}</span>
                    {col.sortable && (
                      <span
                        style={{
                          fontSize: 11,
                          opacity: sort.key === col.key ? 1 : 0.35,
                          transition: "opacity 0.15s",
                          flexShrink: 0,
                        }}
                      >
                        {getSortIcon(col.key)}
                      </span>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {displayData.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length}
                  style={{
                    textAlign: "center",
                    padding: 40,
                    color: "#6b7280",
                    fontSize: 14,
                  }}
                >
                  {data.length === 0
                    ? "Veri bulunamadı."
                    : "Seçilen filtrelere uyan satır yok."}
                </td>
              </tr>
            ) : (
              displayData.map((row, rowIdx) => (
                <tr
                  key={rowIdx}
                  style={{
                    backgroundColor: rowIdx % 2 === 0 ? "#ffffff" : "#f8fafc",
                    borderBottom: "1px solid #e2e8f0",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.backgroundColor = "#eff6ff")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.backgroundColor =
                      rowIdx % 2 === 0 ? "#ffffff" : "#f8fafc")
                  }
                >
                  {columns.map((col) => {
                    const val = row[col.key];
                    return (
                      <td
                        key={col.key}
                        style={{
                          padding: "8px 14px",
                          borderRight: "1px solid #e2e8f0",
                          color:
                            col.key === "TotalCount"
                              ? "#111827"
                              : col.numeric && val > 0
                              ? "#1e3a5f"
                              : "#374151",
                          fontWeight:
                            col.key === "TotalCount" ? 700 : "normal",
                          textAlign: col.numeric ? "right" : "left",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {val === null || val === undefined
                          ? "-"
                          : col.key === "_donePercent"
                          ? (
                            <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 90 }}>
                              <div
                                style={{
                                  flex: 1,
                                  height: 6,
                                  backgroundColor: "#e5e7eb",
                                  borderRadius: 3,
                                  overflow: "hidden",
                                  minWidth: 50,
                                }}
                              >
                                <div
                                  style={{
                                    width: `${val}%`,
                                    height: "100%",
                                    backgroundColor:
                                      val === 100
                                        ? "#22c55e"
                                        : val >= 50
                                        ? "#3b82f6"
                                        : "#f97316",
                                    borderRadius: 3,
                                    transition: "width 0.3s ease",
                                  }}
                                />
                              </div>
                              <span style={{ fontSize: 11, whiteSpace: "nowrap", color: "#374151" }}>
                                {val}%
                              </span>
                            </div>
                          )
                          : col.numeric
                          ? val === 0
                            ? <span style={{ color: "#d1d5db" }}>—</span>
                            : val.toLocaleString("tr-TR")
                          : val}
                      </td>
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>

          {/* Summary footer */}
          {displayData.length > 0 && (
            <tfoot>
              <tr
                style={{
                  backgroundColor: "#e8f0fe",
                  borderTop: "2px solid #93c5fd",
                }}
              >
                {columns.map((col, idx) => {
                  if (idx === 0) {
                    return (
                      <td
                        key={col.key}
                        style={{
                          padding: "9px 14px",
                          fontWeight: 700,
                          color: "#1e3a5f",
                          whiteSpace: "nowrap",
                          fontSize: 12,
                        }}
                      >
                        Toplam ({displayData.length} satır)
                      </td>
                    );
                  }
                  if (!col.numeric || col.noSum) {
                    return (
                      <td
                        key={col.key}
                        style={{
                          padding: "9px 14px",
                          borderRight: "1px solid #bfdbfe",
                        }}
                      />
                    );
                  }
                  const total = displayData.reduce(
                    (sum, row) => sum + (Number(row[col.key]) || 0),
                    0
                  );
                  return (
                    <td
                      key={col.key}
                      style={{
                        padding: "9px 14px",
                        fontWeight: 700,
                        color: "#1e3a5f",
                        textAlign: "right",
                        borderRight: "1px solid #bfdbfe",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {total.toLocaleString("tr-TR")}
                    </td>
                  );
                })}
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────

const CMSAnalysisPage = () => {
  const isTabletOrMobile = useMediaQuery({ query: "(max-width: 700px)" });
  const user = JSON.parse(localStorage.getItem("user"));
  const history = useHistory();

  const [activeTab, setActiveTab] = useState("task"); // "task" | "topic"

  // Both datasets eager-loaded on mount
  const [taskStatusData, setTaskStatusData] = useState([]);
  const [topicStatusData, setTopicStatusData] = useState([]);

  // Independent filters per table — null = no filter (all visible)
  const [taskFilters, setTaskFilters] = useState({ WorkList: null, CourseName: null });
  const [topicFilters, setTopicFilters] = useState({ WorkList: null, CourseName: null, UserName: null });

  const [loader, setLoader] = useState(false);

  const containerStyle = {
    paddingLeft: isTabletOrMobile ? 15 : Components.SideBarWidth + 15,
    paddingTop: 20,
    paddingRight: 15,
    marginBottom: 40,
  };

  // ─── Load ───────────────────────────────────────────────────────────────────

  const loadData = async () => {
    try {
      setLoader(true);
      const res = await GetCMSReport(user.Token);
      if (res.data.ResponseStatus === 1) {
        const d = res.data.ResponseData;
        // Pre-compute % Done so the column can sort like any numeric field
        setTaskStatusData(
          (d.TaskStatus || []).map((row) => ({
            ...row,
            _onErrorWithDone: (row.OnError || 0) + (row.MarkAsDone || 0),
            _donePercent:
              row.TotalCount > 0
                ? Math.round((row.Done / row.TotalCount) * 100)
                : 0,
          }))
        );
        setTopicStatusData(
          (d.TopicStatus || []).map((row) => ({
            ...row,
            _donePercent:
              row.TotalCount > 0
                ? Math.round((row.Success / row.TotalCount) * 100)
                : 0,
          }))
        );
      } else {
        toast.error("CMS raporu alınamadı.");
      }
    } catch (error) {
      console.error("CMSAnalysis loadData error:", error);
      toast.error("Veri yüklenirken hata oluştu.");
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Filter handlers ────────────────────────────────────────────────────────

  const handleTaskFilterChange = (key, val) =>
    setTaskFilters((prev) => ({ ...prev, [key]: val }));

  const handleTopicFilterChange = (key, val) =>
    setTopicFilters((prev) => ({ ...prev, [key]: val }));

  // Sum of OnError across ALL task status rows, ignoring active filters
  const onErrorTotal = useMemo(
    () => taskStatusData.reduce((sum, row) => sum + (row.OnError || 0), 0),
    [taskStatusData]
  );

  // ─── Tabs ───────────────────────────────────────────────────────────────────

  const tabs = [
    { key: "task",  label: "Task Status",  count: taskStatusData.length },
    { key: "topic", label: "Topic Status", count: topicStatusData.length },
  ];

  // ─── Render ─────────────────────────────────────────────────────────────────

  return (
    <>
      {isTabletOrMobile ? (
        <div>
          <span style={{ position: "absolute", top: 10, right: 10 }}>menu</span>
        </div>
      ) : (
        <SideBar />
      )}

      {loader && (
        <div
          style={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 9999,
          }}
        >
          <Loader size={100} />
        </div>
      )}

      <div style={containerStyle}>
        {/* Page header */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 20,
          }}
        >
          <div style={{ fontWeight: "bold", fontSize: 22, color: "#111827" }}>
            CMS Analiz
          </div>
          <button
            onClick={loadData}
            disabled={loader}
            style={{
              padding: "8px 16px",
              backgroundColor: "#2563eb",
              color: "#fff",
              border: "none",
              borderRadius: 6,
              cursor: loader ? "not-allowed" : "pointer",
              fontWeight: 600,
              fontSize: 13,
              opacity: loader ? 0.6 : 1,
            }}
          >
            ↺ Yenile
          </button>
        </div>

        {/* OnError banner — Task Status tab only, when count > 0 */}
        {activeTab === "task" && onErrorTotal > 0 && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              marginBottom: 20,
            }}
          >
            <div
              style={{
                backgroundColor: "#fef2f2",
                border: "1.5px solid #fca5a5",
                borderRadius: 10,
                padding: "14px 28px",
                textAlign: "center",
                boxShadow: "0 2px 8px rgba(239,68,68,0.12)",
              }}
            >
              <div style={{ fontSize: 15, fontWeight: 600, color: "#b91c1c", marginBottom: 8 }}>
                <span style={{ fontSize: 22, fontWeight: 800 }}>{onErrorTotal}</span>
                {" "}adet Hatalı Soru Düzeltilmeyi bekliyor
              </div>
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  history.push("/CMSReports");
                }}
                style={{
                  fontSize: 14,
                  fontWeight: 700,
                  color: "#dc2626",
                  textDecoration: "underline",
                  cursor: "pointer",
                }}
              >
                Hataları Düzelt!
              </a>
            </div>
          </div>
        )}

        {/* Tab bar */}
        <div
          style={{
            display: "flex",
            borderBottom: "2px solid #e5e7eb",
            marginBottom: 20,
          }}
        >
          {tabs.map((tab) => {
            const isActive = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                style={{
                  padding: "10px 22px",
                  border: "none",
                  borderBottom: isActive
                    ? "2px solid #2563eb"
                    : "2px solid transparent",
                  marginBottom: -2,
                  backgroundColor: "transparent",
                  cursor: "pointer",
                  fontWeight: isActive ? 700 : 400,
                  color: isActive ? "#2563eb" : "#6b7280",
                  fontSize: 15,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  transition: "color 0.15s",
                }}
              >
                {tab.label}
                <span
                  style={{
                    backgroundColor: isActive ? "#dbeafe" : "#f3f4f6",
                    color: isActive ? "#1d4ed8" : "#6b7280",
                    borderRadius: 12,
                    padding: "1px 8px",
                    fontSize: 12,
                    fontWeight: 600,
                  }}
                >
                  {tab.count}
                </span>
              </button>
            );
          })}
        </div>

        {/* Active table — sort state resets on tab switch (intentional) */}
        {activeTab === "task" && (
          <AnalysisTable
            columns={TASK_STATUS_COLUMNS}
            data={taskStatusData}
            filters={taskFilters}
            onFilterChange={handleTaskFilterChange}
            filename="CMS_Task_Status.csv"
          />
        )}

        {activeTab === "topic" && (
          <AnalysisTable
            columns={TOPIC_STATUS_COLUMNS}
            data={topicStatusData}
            filters={topicFilters}
            onFilterChange={handleTopicFilterChange}
            filename="CMS_Topic_Status.csv"
          />
        )}
      </div>
    </>
  );
};

export default CMSAnalysisPage;
